import { CommonModule } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, ViewChild } from '@angular/core';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';

@Component({
  selector: 'kg-mobile-branding-slider',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="mobile-branding-slider" *ngIf="images.length">
      <div class="fader" #sliderRef>
        <div [ngStyle]="{ opacity: opacities[idx] }" class="fader__slide" *ngFor="let src of images; let idx = index">
          <img [src]="src" alt="Branding slider image" />
        </div>
        <div class="dots">
          <button
            (click)="slider.moveToIdx(i)"
            *ngFor="let slide of dotHelper; let i = index"
            [class]="'dot ' + (i === currentSlide ? 'active' : '')"
          ></button>
        </div>
      </div>
    </div>
  `,
  styleUrls: ['./mobile-branding-slider.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileBrandingSliderComponent implements AfterViewInit, OnDestroy {
  @Input() images: string[] = [];
  @ViewChild('sliderRef') sliderRef!: ElementRef<HTMLElement>;

  slider!: KeenSliderInstance;
  opacities: number[] = [];
  dotHelper: number[] = [];
  currentSlide: number = 0;

  constructor(private cd: ChangeDetectorRef) {}

  ngAfterViewInit(): void {
    this.initSlider();
  }

  private initSlider(): void {
    if (!this.sliderRef || !this.images.length) {
      return;
    }
    this.slider = new KeenSlider(
      this.sliderRef.nativeElement,
      {
        loop: true,
        slides: this.images.length,
        defaultAnimation: {
          duration: 1500,
        },
        detailsChanged: (s) => {
          this.opacities = s.track.details.slides.map((slide) => slide.portion);
          this.cd.detectChanges();
        },
        slideChanged: (s) => {
          this.currentSlide = s.track.details.rel;
        },
      },
      [(slider) => this.applySliderFade(slider)],
    );
    this.dotHelper = [...Array(this.slider.track.details.slides.length).keys()];
  }

  private applySliderFade(slider: KeenSliderInstance): void {
    let timeout: ReturnType<typeof setTimeout>;
    let mouseOver = false;

    const clearNextTimeout = () => clearTimeout(timeout);
    const nextTimeout = () => {
      clearTimeout(timeout);
      if (mouseOver) return;
      timeout = setTimeout(() => slider.next(), 1500);
    };

    slider.on('created', () => {
      slider.container.addEventListener('mouseover', () => {
        mouseOver = true;
        clearNextTimeout();
      });
      slider.container.addEventListener('mouseout', () => {
        mouseOver = false;
        nextTimeout();
      });
      nextTimeout();
    });
    slider.on('dragStarted', clearNextTimeout);
    slider.on('animationEnded', nextTimeout);
    slider.on('updated', nextTimeout);
  }

  ngOnDestroy(): void {
    if (this.slider) {
      this.slider.destroy();
    }
  }
}
